// frontend/src/App.jsx
import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { authService } from './services/auth';
import { LanguageProvider } from './contexts/LanguageContext';
import { TimezoneProvider } from './contexts/TimezoneContext';
import Login from './features/auth/pages/LoginPage/Login.jsx';
import Dashboard from './features/dashboard/pages/DashboardPage/Dashboard.jsx';
import UserManagement from './features/users/pages/UserManagementPage/UserManagement.jsx';
import InventoryDashboard from './features/inventory/pages/InventoryDashboard.jsx';
import ProductsPage from './features/inventory/pages/ProductsPage.jsx';
import CategoriesPage from './features/inventory/pages/CategoriesPage.jsx';
import SuppliersPage from './features/inventory/pages/SuppliersPage.jsx';
import MovementsPage from './features/inventory/pages/MovementsPage.jsx';
import ProfilePage from './features/profile/pages/ProfilePage/ProfilePage.jsx';
import ForgotPassword from './features/auth/components/ForgotPassword/ForgotPassword.jsx';
import ResetPassword from './features/auth/components/ResetPassword/ResetPassword.jsx';
import ProtectedRoute from './components/common/ProtectedRoute/ProtectedRoute.jsx';

function App() {
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    // Check if user is already authenticated
    const checkAuth = () => {
      const authenticated = authService.isAuthenticated();
      console.log('Authentication check:', authenticated); // Debug log
      setIsAuthenticated(authenticated);
      setLoading(false);
    };

    checkAuth();

    // Listen for storage changes (logout from other tabs)
    const handleStorageChange = (e) => {
      if (e.key === 'token') {
        setIsAuthenticated(!!e.newValue);
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh'
      }}>
        <div>Loading...</div>
      </div>
    );
  }

  return (
    <LanguageProvider>
      <TimezoneProvider>
        <div className="App">
        <Router
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true
          }}
        >
          <Routes>
            {/* Public routes */}
            <Route 
              path="/login" 
              element={
                isAuthenticated ? 
                  <Navigate to="/" replace /> : 
                  <Login onLogin={() => setIsAuthenticated(true)} />
              } 
            />
            <Route 
              path="/forgot-password" 
              element={
                isAuthenticated ? 
                  <Navigate to="/" replace /> : 
                  <ForgotPassword />
              } 
            />
            <Route 
              path="/reset-password/:token" 
              element={
                isAuthenticated ? 
                  <Navigate to="/" replace /> : 
                  <ResetPassword />
              } 
            />
            
            {/* Protected routes */}
            <Route 
              path="/" 
              element={
                <ProtectedRoute isAuthenticated={isAuthenticated}>
                  <Dashboard onLogout={() => setIsAuthenticated(false)} />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/users" 
              element={
                <ProtectedRoute isAuthenticated={isAuthenticated}>
                  <UserManagement onLogout={() => setIsAuthenticated(false)} />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/inventory" 
              element={
                <ProtectedRoute isAuthenticated={isAuthenticated}>
                  <InventoryDashboard onLogout={() => setIsAuthenticated(false)} />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/inventory/products" 
              element={
                <ProtectedRoute isAuthenticated={isAuthenticated}>
                  <ProductsPage onLogout={() => setIsAuthenticated(false)} />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/inventory/categories" 
              element={
                <ProtectedRoute isAuthenticated={isAuthenticated}>
                  <CategoriesPage onLogout={() => setIsAuthenticated(false)} />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/inventory/suppliers" 
              element={
                <ProtectedRoute isAuthenticated={isAuthenticated}>
                  <SuppliersPage onLogout={() => setIsAuthenticated(false)} />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/inventory/movements" 
              element={
                <ProtectedRoute isAuthenticated={isAuthenticated}>
                  <MovementsPage onLogout={() => setIsAuthenticated(false)} />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/profile" 
              element={
                <ProtectedRoute isAuthenticated={isAuthenticated}>
                  <ProfilePage onLogout={() => setIsAuthenticated(false)} />
                </ProtectedRoute>
              } 
            />
            
            {/* Catch all route - redirect to dashboard if authenticated, login if not */}
            <Route 
              path="*" 
              element={
                isAuthenticated ? 
                  <Navigate to="/" replace /> : 
                  <Navigate to="/login" replace />
              } 
            />
          </Routes>
        </Router>
        </div>
      </TimezoneProvider>
    </LanguageProvider>
  );
}

export default App;

